import { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import { UseUpdateComponentRequestVars } from '@frontend/api';
import type { ProcessType, SupplierMaterial } from '@frontend/api';
import {
  Button,
  Flex,
  Text,
  Box,
  ModalFooter,
  Alert,
  AlertTitle,
  AlertIcon,
  AlertDescription,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalButtonGroup,
  ModalCloseButton,
} from '@frontend/components';

import { OP_FACTORY_CLASS_NAMES } from 'modules/processes/data/entities';
import { getProcesses } from 'modules/processes/data/reducer';
import MaterialSelect from 'modules/quotes/quoteItems/components/MaterialSelect';

import {
  getIsMaterialExcluded,
  getSupplierMaterialById,
} from 'store/materials/reducer';

import { confirmModal } from '../data/actions';
import { getComponent, getIsConfirmationModalOpen } from '../data/reducer';
import Process from './Process';
import type { Component } from './types';

export const excludeLegacyProcesses = (
  availableProcesses: ProcessType[],
  selectedProcess: ProcessType | null,
) => {
  const selectedProcessId = selectedProcess ? selectedProcess.id : null;
  return availableProcesses.filter(
    (p) =>
      p.opFactoryClassName !== OP_FACTORY_CLASS_NAMES.MARKETPLACE ||
      p.id === selectedProcessId,
  );
};

const connector = connect(
  (state) => {
    const component = getComponent(state);
    const supplierMaterial =
      component &&
      component.material &&
      getSupplierMaterialById(state, component.material.id);
    return {
      isOpen: getIsConfirmationModalOpen(state),
      allProcesses: getProcesses(state),
      component,
      isMaterialExcluded: getIsMaterialExcluded(
        state,
        (supplierMaterial || {}).materialId,
      ),
      supplierMaterial,
    };
  },
  {
    onClose: confirmModal.close,
  },
);

type ConfirmProcessMaterialProps = {
  allProcesses: ProcessType[];
  isOpen: boolean;
  component: Component;
  isMaterialExcluded: boolean;
  onClose: () => void;
  updateComponentRequest: (update: UseUpdateComponentRequestVars) => void;
  supplierMaterial: null | SupplierMaterial;
};

const ConfirmProcessMaterial = ({
  allProcesses,
  isOpen,
  component,
  isMaterialExcluded,
  onClose,
  updateComponentRequest,
  supplierMaterial,
}: ConfirmProcessMaterialProps) => {
  const { process: selectedProcess, componentId } = component || {};
  const isProcessMaterialUnavailable =
    selectedProcess &&
    !selectedProcess.useGlobalMaterials &&
    supplierMaterial &&
    !selectedProcess.processMaterials?.find(
      (pm) => pm.supplierMaterial.id == supplierMaterial.id,
    );
  const selectedMaterialId =
    !isMaterialExcluded && !isProcessMaterialUnavailable && supplierMaterial
      ? supplierMaterial.materialId
      : null;
  const selectedSupplierMaterialId =
    !isMaterialExcluded && !isProcessMaterialUnavailable && supplierMaterial
      ? supplierMaterial.id
      : null;
  const [processId, setProcessId] = useState((selectedProcess || {}).id);
  const [materialId, setMaterialId] = useState(selectedMaterialId);
  const [supplierMaterialId, setSupplierMaterialId] = useState<
    string | number | null
  >(selectedSupplierMaterialId);

  useEffect(() => {
    if (!processId) {
      setMaterialId(null);
      setSupplierMaterialId(null);
    }
  }, [processId]);

  if (!component) return null;

  const availableProcesses = excludeLegacyProcesses(
    allProcesses,
    selectedProcess,
  );

  const currentSelectedProcess = availableProcesses.find(
    (p) => p.id == processId,
  );

  const handleMaterialChange = ({
    materialId,
    supplierMaterialId,
  }: {
    materialId?: number;
    supplierMaterialId: number | null | string;
  }) => {
    setMaterialId(materialId || null);
    setSupplierMaterialId(supplierMaterialId || null);
  };

  return (
    <Modal isOpen={isOpen} size="sm" onClose={onClose}>
      <ModalContent m={0}>
        <ModalHeader>
          Update Process and Material
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody>
          <Flex flexDirection="column" gap={2}>
            <Box
              position="relative"
              data-testid="update-component-process-container"
            >
              <Text
                color="feature.secondary"
                fontWeight="heavy"
                marginBottom="2px"
                textTransform="uppercase"
              >
                Process
              </Text>
              <Process
                availableProcesses={availableProcesses}
                disabled={false}
                onChange={setProcessId}
                selectedProcessId={processId}
              />
            </Box>
            <Box position="relative">
              <Text
                color="feature.secondary"
                fontWeight="heavy"
                marginBottom="2px"
                textTransform="uppercase"
              >
                Material
              </Text>
              <MaterialSelect
                disabled={!processId}
                onChange={handleMaterialChange}
                process={currentSelectedProcess}
                selectedMaterialId={materialId}
                selectedSupplierMaterialId={supplierMaterialId}
              />
            </Box>
          </Flex>
        </ModalBody>
        <ModalFooter flexDirection="column" gap={1.5}>
          <Alert status="warning">
            <AlertIcon />
            <Flex w="100%" direction="column">
              <AlertTitle>Operations will be regenerated</AlertTitle>
              <AlertDescription display="flex" flexDirection="column" gap={1}>
                <Text>
                  All operations, add-ons, and pricing items will be regenerated
                  from the selected process, and their overrides removed.
                </Text>
                {component.sourceErp && (
                  <Text>
                    All values imported from job #{component.sourceErp} will be
                    lost.
                  </Text>
                )}
                <Text>
                  To change the process and leave everything else, select Update
                  and Keep Existing Ops.
                </Text>
              </AlertDescription>
            </Flex>
          </Alert>
          <ModalButtonGroup>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="secondary"
              isDisabled={!processId}
              onClick={() =>
                updateComponentRequest({
                  componentId: componentId || null,
                  processId: processId || null,
                  materialId: materialId ?? null,
                  supplierMaterialId: supplierMaterialId ?? null,
                })
              }
            >
              Update and keep existing ops
            </Button>
            <Button
              variant="primary"
              onClick={() =>
                updateComponentRequest({
                  componentId: componentId || null,
                  processId: processId || null,
                  materialId: materialId ?? null,
                  supplierMaterialId: supplierMaterialId ?? null,
                  shouldKeepExistingOperations: false,
                })
              }
            >
              Update
            </Button>
          </ModalButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default connector(ConfirmProcessMaterial);
